import { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { CheckCircleIcon } from '@heroicons/react/24/outline';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';
import { useAuth } from '../contexts/AuthContext';
import { doc, setDoc } from 'firebase/firestore';
import { db } from '../firebase/config';
import Logo from '../components/Logo';
import { countries } from '../data/countries';

function Signup() {
  const navigate = useNavigate();
  const { register } = useAuth();
  
  const [formData, setFormData] = useState({
    fullName: '',
    country: '',
    address: '',
    mobileNumber: '',
    email: '',
    password: '',
    confirmPassword: '',
    amount: '',
    idCard: null
  });

  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [registrationDetails, setRegistrationDetails] = useState(null);

  const handleInputChange = (e) => {
    const { name, value, files } = e.target;
    if (name === 'idCard' && files) {
      setFormData(prev => ({
        ...prev,
        [name]: files[0]
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const uploadImage = async (file) => {
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      throw new Error('Please upload a valid image file. We accept JPG, PNG, or GIF formats.');
    }

    // Validate file size (max 32MB)
    const maxSize = 32 * 1024 * 1024; // 32MB in bytes
    if (file.size > maxSize) {
      throw new Error('The image file is too large. Please upload an image smaller than 32MB.');
    }

    // Convert image file to base64
    const base64Image = await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result.split(',')[1]);
      reader.onerror = () => reject(new Error('Failed to read the image file. Please try again.'));
    });

    const formData = new FormData();
    formData.append('key', 'a907ff7558d4a37dc1c917aa410bbb4c');
    formData.append('image', base64Image);

    try {
      console.log('Sending request to ImgBB...');
      const response = await fetch('https://api.imgbb.com/1/upload', {
        method: 'POST',
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error('ImgBB API Error:', errorData);
        throw new Error('Unable to upload your ID card image. Please try again in a few moments.');
      }

      const data = await response.json();
      console.log('ImgBB Response:', data);

      if (!data.success) {
        throw new Error('Failed to process your ID card image. Please try again.');
      }

      if (!data.data || !data.data.url) {
        throw new Error('Invalid response from image upload service. Please try again.');
      }

      // Ensure we have a valid URL
      const imageUrl = data.data.url;
      if (typeof imageUrl !== 'string' || !imageUrl.startsWith('http')) {
        throw new Error('Invalid image URL received. Please try again.');
      }

      console.log('Image upload successful:', imageUrl);
      return imageUrl;
    } catch (error) {
      console.error('Image upload error:', error);
      if (error.message.includes('Failed to fetch')) {
        throw new Error('Network error: Unable to connect to the image upload service. Please check your internet connection and try again.');
      }
      throw error;
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Validate passwords
    if (formData.password !== formData.confirmPassword) {
      setError('The passwords you entered do not match. Please make sure both passwords are identical.');
      setIsLoading(false);
      return;
    }

    const passwordErrors = validatePassword(formData.password);
    if (passwordErrors.length > 0) {
      setError(
        <div className="space-y-2">
          <p className="font-medium">Please fix the following password requirements:</p>
          <ul className="list-disc list-inside">
            {passwordErrors.map((error, index) => (
              <li key={index} className="text-sm">{error}</li>
            ))}
          </ul>
        </div>
      );
      setIsLoading(false);
      return;
    }

    // Validate form
    if (!formData.idCard) {
      setError('Please upload a clear image of your ID card to complete the registration.');
      setIsLoading(false);
      return;
    }

    try {
      // Upload ID card image first
      let idCardUrl = '';
      if (formData.idCard) {
        try {
          console.log('Starting ID card upload...');
        idCardUrl = await uploadImage(formData.idCard);
          console.log('ID Card uploaded successfully:', idCardUrl);
          
          // Validate that we got a valid URL
          if (!idCardUrl || typeof idCardUrl !== 'string') {
            throw new Error('Failed to get a valid URL for the ID card image.');
          }
        } catch (error) {
          console.error('ID Card upload error:', error);
          setError(`ID Card Upload Error: ${error.message}`);
          setIsLoading(false);
          return;
        }
      }

      // Ensure we have a valid URL before proceeding
      if (!idCardUrl) {
        setError('Failed to upload ID card. Please try again.');
        setIsLoading(false);
        return;
      }

      // Register user with auth
      console.log('Starting user registration...');
      const result = await register({
        ...formData,
        idCardUrl: idCardUrl
      });
      console.log('User registration successful:', result);
      
      setRegistrationDetails({
        ...formData,
        password: undefined,
        confirmPassword: undefined,
        registrationDate: new Date().toISOString(),
        otpCodes: result.otpCodes,
        idCardUrl: idCardUrl
      });
      setIsSubmitted(true);
    } catch (err) {
      console.error('Signup error:', {
        code: err.code,
        message: err.message,
        stack: err.stack
      });
      
      let errorMessage = 'Failed to create your account. ';
      
      if (err.code === 'auth/email-already-in-use') {
        errorMessage = 'This email address is already registered. Please use a different email or try logging in.';
      } else if (err.code === 'auth/invalid-email') {
        errorMessage = 'Please enter a valid email address.';
      } else if (err.code === 'auth/weak-password') {
        errorMessage = 'Your password is too weak. Please use a stronger password.';
      } else if (err.code === 'auth/network-request-failed') {
        errorMessage = 'Network error: Unable to connect to the server. Please check your internet connection and try again.';
      } else if (err.message.includes('ID Card Upload Error')) {
        errorMessage = err.message;
      } else if (err.message.includes('Failed to send email')) {
        errorMessage = 'Account created but failed to send OTP codes. Please contact support.';
      }
      
      setError(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const validatePassword = (password) => {
    const minLength = 8;
    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumbers = /\d/.test(password);
    const hasSpecialChar = /[!@#$%^&*(),.?":{}|<>]/.test(password);

    const errors = [];
    if (password.length < minLength) errors.push('• Password must be at least 8 characters long');
    if (!hasUpperCase) errors.push('• Include at least one uppercase letter (A-Z)');
    if (!hasLowerCase) errors.push('• Include at least one lowercase letter (a-z)');
    if (!hasNumbers) errors.push('• Include at least one number (0-9)');
    if (!hasSpecialChar) errors.push('• Include at least one special character (!@#$%^&*(),.?":{}|<>)');

    return errors;
  };

  if (isSubmitted) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Navigation />
        <div className="max-w-4xl mx-auto px-4 py-16 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl shadow-xl p-8">
            <div className="text-center">
              <motion.div
                initial={{ scale: 0 }}
                animate={{ scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <CheckCircleIcon className="mx-auto h-16 w-16 text-primary-600" />
              </motion.div>
              <h2 className="mt-6 text-3xl font-bold text-gray-900">Registration Successful!</h2>
              <p className="mt-4 text-lg text-gray-600">
                Thank you for registering with WealthQuota Investment Group LLC.
              </p>
              
              {/* OTP Information */}
              <div className="mt-8 bg-gray-50 p-6 rounded-lg border border-gray-200">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Important Security Information</h3>
                <div className="space-y-4">
                  <div className="bg-white p-4 rounded-md border border-primary-100">
                    <p className="font-medium text-gray-900 mb-2">Login OTP</p>
                    <p className="font-mono text-lg bg-primary-50 p-2 rounded">
                      {registrationDetails.otpCodes?.loginOTP}
                    </p>
                  </div>
                  <div className="bg-white p-4 rounded-md border border-primary-100">
                    <p className="font-medium text-gray-900 mb-2">Withdrawal OTP</p>
                    <p className="font-mono text-lg bg-primary-50 p-2 rounded">
                      {registrationDetails.otpCodes?.withdrawalOTP}
                    </p>
                  </div>
                  <div className="mt-4 text-sm text-red-600">
                    <p>⚠️ Please save these OTP codes securely. They will be required for:</p>
                    <ul className="list-disc list-inside mt-2">
                      <li>Logging into your account</li>
                      <li>Processing withdrawals</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Registration Summary */}
              <div className="mt-8 bg-gray-50 rounded-lg p-6 text-left">
                <h3 className="text-xl font-semibold text-gray-900 mb-4">Registration Summary</h3>
                <div className="space-y-3">
                  <p className="text-gray-600">
                    <span className="font-medium">Name:</span> {registrationDetails.fullName}
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium">Email:</span> {registrationDetails.email}
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium">Investment Amount:</span> ${parseFloat(registrationDetails.amount).toLocaleString()}
                  </p>
                  <p className="text-gray-600">
                    <span className="font-medium">Registration Date:</span> {new Date(registrationDetails.registrationDate).toLocaleDateString()}
                  </p>
                </div>
              </div>

              <div className="mt-8 space-y-4">
                <p className="text-gray-600">
                  We will review your application and send you:
                </p>
                <ul className="text-left max-w-md mx-auto space-y-2">
                  <li className="flex items-center text-gray-600">
                    <CheckCircleIcon className="h-5 w-5 text-primary-600 mr-2" />
                    Your account credentials
                  </li>
                  <li className="flex items-center text-gray-600">
                    <CheckCircleIcon className="h-5 w-5 text-primary-600 mr-2" />
                    Dashboard access instructions
                  </li>
                  <li className="flex items-center text-gray-600">
                    <CheckCircleIcon className="h-5 w-5 text-primary-600 mr-2" />
                    Investment plan details
                  </li>
                  <li className="flex items-center text-gray-600">
                    <CheckCircleIcon className="h-5 w-5 text-primary-600 mr-2" />
                    Next steps for account funding
                  </li>
                </ul>
              </div>

              <div className="mt-8 space-x-4">
                <Link to="/">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="inline-flex items-center px-6 py-3 border border-transparent text-base font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700"
                  >
                    Return to Homepage
                  </motion.button>
                </Link>
                <Link to="/contact">
                  <motion.button
                    whileHover={{ scale: 1.05 }}
                    whileTap={{ scale: 0.95 }}
                    className="inline-flex items-center px-6 py-3 border border-primary-600 text-base font-medium rounded-md text-primary-600 hover:bg-primary-50"
                  >
                    Contact Support
                  </motion.button>
                </Link>
              </div>
            </div>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-700 to-primary-900 flex flex-col">
      <Navigation />
      <div className="flex-grow flex flex-col justify-center py-12 sm:px-6 lg:px-8">
        <div className="sm:mx-auto sm:w-full sm:max-w-md">
          <h2 className="text-center text-3xl font-extrabold text-white">
            Create your account
          </h2>
          <p className="mt-2 text-center text-sm text-primary-100">
            Join thousands of successful investors
          </p>
        </div>

        <div className="mt-8 sm:mx-auto sm:w-full sm:max-w-md">
          <div className="bg-white/10 backdrop-blur-lg py-8 px-4 shadow-2xl sm:rounded-lg sm:px-10 border border-white/20">
            {error && (
              <div className="mb-4 bg-error-50 border border-error-200 text-error-600 px-4 py-3 rounded">
                {error}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-white">
                  Email address
                </label>
                <div className="mt-1">
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    value={formData.email}
                    onChange={handleInputChange}
                    className="appearance-none block w-full px-3 py-2 border border-white/20 bg-white/5 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-secondary-500 focus:border-secondary-500 text-white sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="fullName" className="block text-sm font-medium text-white">
                  Full Name
                </label>
                <div className="mt-1">
                  <input
                    id="fullName"
                    name="fullName"
                    type="text"
                    required
                    value={formData.fullName}
                    onChange={handleInputChange}
                    className="appearance-none block w-full px-3 py-2 border border-white/20 bg-white/5 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-secondary-500 focus:border-secondary-500 text-white sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-white">
                  Phone Number
                </label>
                <div className="mt-1">
                  <input
                    id="phone"
                    name="mobileNumber"
                    type="tel"
                    required
                    value={formData.mobileNumber}
                    onChange={handleInputChange}
                    className="appearance-none block w-full px-3 py-2 border border-white/20 bg-white/5 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-secondary-500 focus:border-secondary-500 text-white sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="country" className="block text-sm font-medium text-white">
                  Country
                </label>
                <div className="mt-1">
                  <select
                    id="country"
                    name="country"
                    required
                    value={formData.country}
                    onChange={handleInputChange}
                    className="appearance-none block w-full px-3 py-2 border border-white/20 bg-white/5 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-secondary-500 focus:border-secondary-500 text-white sm:text-sm"
                  >
                    <option value="">Select your country</option>
                    {countries.map((country) => (
                      <option key={country} value={country} className="bg-primary-800">
                        {country}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <div>
                <label htmlFor="idCard" className="block text-sm font-medium text-white">
                  ID Card Image
                </label>
                <div className="mt-1">
                  <input
                    id="idCard"
                    name="idCard"
                    type="file"
                    accept="image/*"
                    required
                    onChange={handleInputChange}
                    className="appearance-none block w-full px-3 py-2 border border-white/20 bg-white/5 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-secondary-500 focus:border-secondary-500 text-white sm:text-sm file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-secondary-500 file:text-white hover:file:bg-secondary-600"
                  />
                  <p className="mt-1 text-xs text-primary-200">
                    Upload a clear image of your ID card (passport, driver's license, or national ID)
                  </p>
                </div>
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-white">
                  Password
                </label>
                <div className="mt-1">
                  <input
                    id="password"
                    name="password"
                    type="password"
                    required
                    value={formData.password}
                    onChange={handleInputChange}
                    className="appearance-none block w-full px-3 py-2 border border-white/20 bg-white/5 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-secondary-500 focus:border-secondary-500 text-white sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-white">
                  Confirm Password
                </label>
                <div className="mt-1">
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    required
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    className="appearance-none block w-full px-3 py-2 border border-white/20 bg-white/5 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-secondary-500 focus:border-secondary-500 text-white sm:text-sm"
                  />
                </div>
              </div>

              <div>
                <button
                  type="submit"
                  disabled={isLoading}
                  className={`w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-secondary-500 hover:bg-secondary-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-500 ${
                    isLoading ? 'opacity-50 cursor-not-allowed' : ''
                  }`}
                >
                  {isLoading ? (
                    <svg className="animate-spin h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                  ) : (
                    'Sign up'
                  )}
                </button>
              </div>
            </form>

            <div className="mt-6">
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-white/20"></div>
                </div>
                <div className="relative flex justify-center text-sm">
                  <span className="px-2 bg-primary-800 text-primary-200">
                    Already have an account?
                  </span>
                </div>
              </div>

              <div className="mt-6">
                <button
                  onClick={() => navigate('/login')}
                  className="w-full flex justify-center py-2 px-4 border border-white/20 rounded-md shadow-sm text-sm font-medium text-white bg-transparent hover:bg-white/5 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-secondary-500"
                >
                  Sign in
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Signup; 